<template>
    <PanelPage>
        <template v-slot:title>
            <SmartBreadcrumbs
                :pages="[
                    {
                        name: $tc('messages.activities', 2),
                        href: meta.links.index,
                    },
                    {
                        name: activity.title,
                        href: activity.links.show,
                    },
                    {
                        name: $t('messages.assign_evaluation_value'),
                        href: null,
                    },
                ]"
            />
        </template>

        <span>
            {{ $tc('messages.evaluation_criteria', 2) }}
        </span>

        <div class="flex justify-end space-x-2 items-center">
            <EvaluationCriteriaCreateForm
                :options="options"
                :url="meta.links.evaluation_criteria_store"
                :defaults="activity.defaults"
            />

            <form :action="meta.links.update" method="post" @submit.prevent="submit">
                <SubmitFormButton :button-text="$t('messages.save')" :processing="form.processing" />
            </form>
        </div>

        <div class="flex justify-end">
            <transition
                enter-active-class="transition ease-linear duration-200"
                enter-from-class="opacity-0"
                enter-to-class="opacity-100"
                leave-active-class="transition ease-linear duration-150"
                leave-from-class="opacity-100"
                leave-to-class="opacity-0"
            >
                <AlertMessage class="mt-5 inline-block" v-if="evaluationCriteriaWithGradesThatWillBeDeleted.length">
                    <template v-slot:title>
                        {{ $t('messages.warning') }}
                    </template>
                    <template v-slot:body>
                        <div
                            v-for="evaluationCriteria in evaluationCriteriaWithGradesThatWillBeDeleted"
                            :key="evaluationCriteria.evaluation_criterion_id"
                        >
                            {{ evaluationCriteria.name }}: {{ evaluationCriteria.confirm_deletion_message }}
                        </div>
                    </template>
                </AlertMessage>
            </transition>
        </div>

        <div class="mt-3">
            <label
                v-for="category in options.categories"
                :key="category.id"
                :class="selected.includes(category.id) ? 'bg-indigo-100 text-indigo-800' : 'bg-gray-100 text-gray-600'"
                class="cursor-pointer mr-3 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
            >
                <input :id="category.id" :value="category.id" type="checkbox" v-model="selected" class="hidden" />
                <span>
                    {{ category.name }}
                </span>
            </label>
        </div>

        <div class="flex flex-col mt-5">
            <div class="-my-2 py-2 overflow-x-auto sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
                <div
                    class="align-middle inline-block min-w-full shadow overflow-hidden sm:rounded-lg border-b border-gray-200"
                >
                    <table class="min-w-full divide-y divide-gray-200">
                        <thead>
                            <tr>
                                <th
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    -
                                </th>
                                <th
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.title') }}
                                </th>
                                <th
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.description') }}
                                </th>
                                <th
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $tc('messages.grade_level', 2) }}
                                </th>
                                <th
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $tc('messages.dimensions', 2) }}
                                </th>
                                <th
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    <span class="sr-only">{{ $t('messages.actions') }}</span>
                                </th>
                            </tr>
                        </thead>
                        <TransitionGroup
                            enter-active-class="transition-opacity ease-linear duration-300"
                            enter-from-class="opacity-0"
                            enter-to-class="opacity-100"
                            leave-active-class="transition-opacity ease-linear duration-300"
                            leave-from-class="opacity-100"
                            leave-to-class="opacity-0"
                            tag="tbody"
                        >
                            <tr
                                :class="index % 2 === 0 ? 'bg-white' : 'bg-gray-50'"
                                v-for="(evaluationCriterion, index) in filteredEvaluationCriteria"
                                :key="evaluationCriterion.id"
                            >
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                    <label class="sr-only">{{ evaluationCriterion.title }}</label>
                                    <input
                                        v-model="form.evaluation_criteria"
                                        :value="evaluationCriterion.id"
                                        type="checkbox"
                                        class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded mr-1"
                                    />
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                    {{ evaluationCriterion.title }}
                                </td>
                                <td class="px-6 py-4 text-sm leading-5 text-gray-500">
                                    {{ evaluationCriterion.description }}
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                    {{ evaluationCriterion.grade_level_names }}
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                    <div
                                        v-for="dimension in evaluationCriterion.dimension_abbreviations"
                                        :key="dimension.id"
                                        class="inline-block mr-1"
                                    >
                                        <span :title="dimension.title">{{ dimension.abbreviation }}</span>
                                    </div>
                                    <div class="flex flex-wrap space-x-1">
                                        <BadgePill
                                            v-for="category in evaluationCriterion.categories"
                                            :key="category.id"
                                        >
                                            {{ category.name }}
                                        </BadgePill>
                                    </div>
                                </td>
                                <td
                                    class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500 flex space-x-2 justify-end"
                                >
                                    <InertiaSquareLinkButton action="edit" :href="evaluationCriterion.links.edit" />
                                </td>
                            </tr>
                        </TransitionGroup>
                    </table>
                </div>
            </div>
        </div>

        <div class="flex justify-end">
            <transition
                enter-active-class="transition ease-linear duration-200"
                enter-from-class="opacity-0"
                enter-to-class="opacity-100"
                leave-active-class="transition ease-linear duration-150"
                leave-from-class="opacity-100"
                leave-to-class="opacity-0"
            >
                <AlertMessage class="mt-5 inline-block" v-if="evaluationCriteriaWithGradesThatWillBeDeleted.length">
                    <template v-slot:title>
                        {{ $t('messages.warning') }}
                    </template>
                    <template v-slot:body>
                        <div
                            v-for="evaluationCriteria in evaluationCriteriaWithGradesThatWillBeDeleted"
                            :key="evaluationCriteria.evaluation_criterion_id"
                        >
                            {{ evaluationCriteria.name }}: {{ evaluationCriteria.confirm_deletion_message }}
                        </div>
                    </template>
                </AlertMessage>
            </transition>
        </div>

        <form :action="meta.links.update" method="post" @submit.prevent="submit">
            <div class="flex justify-end mt-5">
                <SubmitFormButton :button-text="$t('messages.save')" :processing="form.processing" />
            </div>
        </form>
    </PanelPage>
</template>

<script>
import Layout from '@/Shared/Layout.vue';
import PanelPage from '@/Shared/PanelPage.vue';
import InertiaSquareLinkButton from '@/components/InertiaSquareLinkButton.vue';
import SubmitFormButton from '@/Pages/Teacher/Activities/Subactivities/SubmitFormButton.vue';
import EvaluationCriteriaCreateForm from '@/components/pageSpecific/EvaluationCriteriaCreateForm.vue';
import BadgePill from '@/components/BadgePill.vue';
import { TransitionGroup } from 'vue';
import AlertMessage from '@/components/AlertMessage.vue';

export default {
    components: {
        SubmitFormButton,
        InertiaSquareLinkButton,
        PanelPage,
        EvaluationCriteriaCreateForm,
        BadgePill,
        TransitionGroup,
        AlertMessage,
    },
    props: ['activity', 'meta', 'options', 'evaluationCriteria'],
    name: 'Edit',
    layout: Layout,
    data() {
        return {
            form: this.$inertia.form({
                evaluation_criteria: this.activity.evaluation_criteria,
            }),
            selected: this.activity.category_ids,
        };
    },
    methods: {
        submit() {
            this.form.patch(this.meta.links.update);
        },
    },
    computed: {
        filteredEvaluationCriteria() {
            return this.evaluationCriteria.filter((e) => {
                if (this.form.evaluation_criteria.includes(e.id)) {
                    return true;
                }
                return this.selected.some((c) => e.category_ids.includes(c));
            });
        },
        evaluationCriteriaWithGradesThatWillBeDeleted() {
            return this.activity.evaluationCriteriaWithGrades.filter((e) => {
                return !this.form.evaluation_criteria.includes(e.evaluation_criterion_id);
            });
        },
    },
};
</script>
